import { useState, useImperativeHandle, useCallback } from "react";
import { ValidationGroupContext } from "./ValidationGroupContext";
import type { Validation } from "@/types/Validations";
import { getCurrentStateValues } from "@/stores";
import { type ValidationGroupProviderProps } from "./ValidationGroupProviderProps";

export function ValidationGroupProvider({
  children,
  ref,
  name,
  isValidated: isValidatedProp = false,
}: ValidationGroupProviderProps) {
  const [isValidated, setIsValidated] = useState(isValidatedProp);
  const [validations, setValidations] = useState<Record<string, Validation>>(
    {},
  );
  const [errors, setErrors] = useState<Record<string, string | null>>({});

  useImperativeHandle(ref, () => ({
    validate: async () => {
      setIsValidated(true);
      let hasBeenFocused = false;
      let isValid = true;
      const currentState = getCurrentStateValues();
      const errors = {} as Record<string, string | null>;

      Object.entries(validations).forEach(([name, validation]) => {
        const result = validation.validate(currentState);
        if (!result.isValid) {
          isValid = false;
          errors[name] = result.error!;
        } else {
          errors[name] = null;
        }

        if (!result.isValid && !hasBeenFocused) {
          if (validation.ref?.current) {
            hasBeenFocused = true;
            validation.ref.current.focus();
          }
        }
      });

      return { isValid };
    },
  }));

  const registerValidation = useCallback(
    (name: string, validation: Validation): (() => void) => {
      setValidations((prev) => ({ ...prev, [name]: validation }));

      return () => {
        setValidations((prev) => {
          const newValidations = { ...prev };
          delete newValidations[name];
          return newValidations;
        });
      };
    },
    [],
  );

  return (
    <ValidationGroupContext.Provider
      value={{
        isValidated,
        registerValidation,
        groupName: name,
        errors,
        setErrors,
      }}
    >
      {children}
    </ValidationGroupContext.Provider>
  );
}
